import React from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    alpha,
    useTheme,
} from "@mui/material";
import { ArrowForward, Close as CloseIcon, Phone as PhoneIcon } from "@mui/icons-material";
import { LUXURY } from "data/guestLuxuryTheme";

const CustomerLookupDialog = ({
    open,
    isMobile,
    companyName,
    mobileValue,
    onMobileChange,
    isMobileInvalid,
    onSkip,
    onContinue,
    isLoading,
    continueDisabled,
    buttonGradient,
    buttonGradientHover,
    buttonShadow,
    buttonShadowHover,
    luxury = false,
}) => {
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === "backdropClick") return;
                if (reason === "escapeKeyDown") return;
            }}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2.5,
                    overflow: "hidden",
                    boxShadow: isMobile ? "none" : "0 24px 50px rgba(15,23,42,0.24)",
                },
            }}
        >
            <Box
                sx={{
                    px: 2.25,
                    py: 2,
                    color: "#ffffff",
                    background: luxury ? LUXURY.barGradient : "linear-gradient(135deg, #6b7dcf 0%, #5568d3 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <Box>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        Welcome to {companyName || "our restaurant"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: alpha("#ffffff", 0.88), mt: 0.35 }}>
                        Enter your mobile to load profile and loyalty points
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onSkip}
                    sx={{
                        color: "#ffffff",
                        bgcolor: alpha("#ffffff", 0.16),
                        "&:hover": { bgcolor: alpha("#ffffff", 0.24) },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2.25, bgcolor: luxury ? LUXURY.page : "#f8fafc" }}>
                <Box
                    sx={{
                        p: 1.6,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                        bgcolor: "#ffffff",
                        boxShadow: "0 4px 16px rgba(15,23,42,0.05)",
                    }}
                >
                    <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1.1 }}>
                        Customer mobile number
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. 0713509576"
                        value={mobileValue}
                        onChange={(e) => onMobileChange(e.target.value)}
                        error={isMobileInvalid}
                        helperText={
                            isMobileInvalid ? "Enter a valid mobile number (10-15 digits)" : " "
                        }
                        inputProps={{ inputMode: "numeric", maxLength: 15 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PhoneIcon sx={{ color: "#64748b", fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 1.8,
                                bgcolor: "#ffffff",
                            },
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") onContinue();
                        }}
                    />

                    <Stack direction="row" spacing={1} sx={{ mt: 1.6 }}>
                        <Button
                            onClick={onSkip}
                            variant="outlined"
                            fullWidth
                            sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 1.8,
                                py: 1,
                                borderColor: alpha(theme.palette.divider, 0.7),
                                color: "#64748b",
                            }}
                        >
                            Skip for now
                        </Button>
                        <Button
                            onClick={onContinue}
                            variant="contained"
                            disabled={isLoading || continueDisabled}
                            fullWidth
                            endIcon={!isLoading ? <ArrowForward sx={{ fontSize: "1rem !important" }} /> : null}
                            sx={{
                                textTransform: "none",
                                fontWeight: 800,
                                borderRadius: 1.8,
                                py: 1,
                                background: buttonGradient,
                                boxShadow: buttonShadow,
                                "&:hover": {
                                    background: buttonGradientHover,
                                    boxShadow: buttonShadowHover,
                                },
                            }}
                        >
                            {isLoading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                                    Checking...
                                </Box>
                            ) : (
                                "Continue"
                            )}
                        </Button>
                    </Stack>
                </Box>

                <Typography sx={{ mt: 1.2, textAlign: "center", color: "#64748b", fontSize: "0.75rem" }}>
                    We use this to find your profile, loyalty points, and faster checkout.
                </Typography>
            </DialogContent>
        </Dialog>
    );
};

export default CustomerLookupDialog;
